import { generateUID } from '@aytm/lib/common/uid'
import PollingService from '../../common/PollingService'
import mapMessages from '../../common/mapMessages'
import { trackAgentResponses } from '../../common/pendoTracking'
import { SKIPPER_AUTHOR_ID } from '../../const'

import { API_CHECK_ENDPOINT, DEFAULT_POLL_MS, IDLE_POLL_MS } from './const'
import { PollingServiceParams, AssistantPollingServiceData, SharedAssistantState } from './types'

export default function buildPollingService<T extends SharedAssistantState>({
  assistantState,
  update,
  awaitContext
}: PollingServiceParams<T>) {
  const service = PollingService()

  service.url = () => API_CHECK_ENDPOINT.URL.replace('{chatID}', String(assistantState.chatID))
  service.method = API_CHECK_ENDPOINT.method
  service.fetchBody = async () => {
    const context = await awaitContext({ assistantState })
    return {
      domain: assistantState.domain,
      context,
      ids: assistantState.messages.map(m => m.id)
    }
  }
  service.onConnectionStatusChange = status => {
    if (assistantState.connectionStatus !== status) {
      assistantState.connectionStatus = status
      update()
    }
  }
  service.onResponse = response => {
    // 404 means the chat is gone (e.g. closed in another tab); surface it and stop the endless poll
    if (response.status === 404) {
      assistantState.messages = [
        ...assistantState.messages,
        {
          error: true,
          id: generateUID(),
          timestamp: Date.now(),
          text: 'An error occurred - please [reload the page](#reload_page)',
          author: SKIPPER_AUTHOR_ID
        }
      ]
      assistantState.inputReady = false
      update()
      service.stop()
      return false
    }
    return response.status === 200 || response.status === 304
  }
  service.onData = (data: AssistantPollingServiceData) => {
    let doUpdate = false

    if (Array.isArray(data.messages)) {
      assistantState.messages = mapMessages(data.messages, assistantState.messages)
      trackAgentResponses({
        domain: assistantState.domain,
        conversationId: assistantState.chatID,
        messages: assistantState.messages
      })
      doUpdate = true
    }

    if (data.inputReady != null) {
      assistantState.inputReady = data.inputReady
      doUpdate = true
    }

    if (data.closed != null) {
      assistantState.closed = data.closed
      doUpdate = true
    }

    if (data.rated != null) {
      assistantState.rated = data.rated
      doUpdate = true
    }

    if (doUpdate) {
      update()
    }

    if (data.pollMs != null) {
      return data.pollMs
    }
    return assistantState.inputReady ? IDLE_POLL_MS : DEFAULT_POLL_MS
  }

  return service
}
